import { useCallback, useRef, useState } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { ButtonWithDropdown } from '@simplywallst/ui-core';
import BinSolid from '@simplywallst/ui-core/icons/BinSolid';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';
import MoreHorizontalSolid from '@simplywallst/ui-core/icons/MoreHorizontalSolid';

import type { Watchlist } from '@/hooks/useWatchlist';

const DeleteWatchlistModal = loadable(
  () => import('../../../DeleteWatchlistModal')
);
const RenameWatchlistModal = loadable(
  () => import('../../../RenameWatchlistModal')
);

interface Props {
  watchlist: Watchlist;
}

enum SelectOptions {
  RENAME_LIST = 'rename-list',
  DELETE_LIST = 'delete-list',
}

type Modal = 'renameList' | 'deleteList';

export const MoreOptions = ({ watchlist }: Props) => {
  const [modal, setModal] = useState<Modal | null>(null);
  const { trackEvent } = useTracking();

  const handleCloseModal = useCallback(() => setModal(null), []);

  const handleSelect = useCallback((value: string) => {
    if (value === SelectOptions.RENAME_LIST) {
      setModal('renameList');
    } else if (value === SelectOptions.DELETE_LIST) {
      setModal('deleteList');
    }
  }, []);

  const { current: options } = useRef([
    {
      label: 'Rename List',
      value: SelectOptions.RENAME_LIST,
      icon: <EditSolid />,
    },

    {
      label: 'Delete List',
      value: SelectOptions.DELETE_LIST,
      icon: <BinSolid />,
    },
  ]);

  return (
    <>
      <ButtonWithDropdown
        styleType="passive"
        data-cy-id="watchlist-header-more"
        options={options}
        onSelect={handleSelect}
        onClick={() => {
          trackEvent({
            action: 'click',
            modifier: 'watchlistMoreOptions',
          });
        }}
      >
        <MoreHorizontalSolid />
      </ButtonWithDropdown>

      {modal === 'renameList' && (
        <RenameWatchlistModal
          watchlist={watchlist}
          onClose={handleCloseModal}
          onRename={handleCloseModal}
        />
      )}

      {modal === 'deleteList' && (
        <DeleteWatchlistModal
          watchlist={watchlist}
          onClose={handleCloseModal}
          onDelete={handleCloseModal}
        />
      )}
    </>
  );
};
